import {
  Box,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";

interface FormData {
  name: string;
  surname: string;
  phone: string;
  email: string;
  businessName: string;
  organisationCategory: string;
  agreeTerms: boolean;
}

interface FormErrors {
  name?: string;
  surname?: string;
  phone?: string;
  email?: string;
  businessName?: string;
  organisationCategory?: string;
  agreeTerms?: string;
}

interface Props {
  formData: FormData;
  errors: FormErrors;
  onChange: (field: keyof FormData, value: string | boolean) => void;
  onNext: () => void;
}

const categories = [
  { label: "Funeral Parlour", value: "funeral_parlor" },
  {
    label: "Church / Religious Organisation",
    value: "church/religious_organisation",
  },
  { label: "Community Organisation", value: "community_organization" },
  { label: "Insurance Company", value: "insurance_company" },
  { label: "Individual Consultant", value: "individual_consultant" },
  { label: "Other", value: "other" },
];

export default function PersonalInfoForm({
  formData,
  errors,
  onChange,
  onNext,
}: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.primary[800],
      borderRadius: "16px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": { border: "none" },
    },
  };

  const labelSx = {
    fontWeight: 600,
    color: colors.primary[700],
    mb: 0.5,
    fontSize: "0.88rem",
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2" sx={labelSx}>
            Name *
          </Typography>
          <TextField
            fullWidth
            placeholder="Your name here"
            size="small"
            value={formData.name}
            onChange={(e) => onChange("name", e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            sx={fieldSx}
          />
        </Box>
        <Box>
          <Typography variant="body2" sx={labelSx}>
            Surname *
          </Typography>
          <TextField
            fullWidth
            placeholder="Placeholder"
            size="small"
            value={formData.surname}
            onChange={(e) => onChange("surname", e.target.value)}
            error={!!errors.surname}
            helperText={errors.surname}
            sx={fieldSx}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2" sx={labelSx}>
            Phone Number
          </Typography>
          <TextField
            fullWidth
            placeholder="+263"
            size="small"
            value={formData.phone}
            onChange={(e) => onChange("phone", e.target.value)}
            error={!!errors.phone}
            helperText={errors.phone}
            sx={fieldSx}
          />
        </Box>
        <Box>
          <Typography variant="body2" sx={labelSx}>
            Email
          </Typography>
          <TextField
            fullWidth
            placeholder="Placeholder"
            size="small"
            value={formData.email}
            onChange={(e) => onChange("email", e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            sx={fieldSx}
          />
        </Box>
      </Box>

      <Box>
        <Typography variant="body2" sx={labelSx}>
          Business / Consultant Name *
        </Typography>
        <TextField
          fullWidth
          placeholder="Placeholder"
          size="small"
          value={formData.businessName}
          onChange={(e) => onChange("businessName", e.target.value)}
          error={!!errors.businessName}
          helperText={errors.businessName}
          sx={fieldSx}
        />
      </Box>

      <Box>
        <Typography variant="body2" sx={labelSx}>
          Organisation Category
        </Typography>
        <FormControl
          fullWidth
          size="small"
          error={!!errors.organisationCategory}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: colors.primary[800],
              borderRadius: "16px",
              "& fieldset": { border: "none" },
            },
          }}
        >
          <Select
            displayEmpty
            value={formData.organisationCategory}
            onChange={(e) => onChange("organisationCategory", e.target.value)}
            renderValue={(selected) => {
              if (!selected)
                return (
                  <Typography sx={{ color: "#A0938D" }}>Placeholder</Typography>
                );
              return (
                categories.find((c) => c.value === selected)?.label || selected
              );
            }}
          >
            {categories.map((cat) => (
              <MenuItem key={cat.value} value={cat.value}>
                {cat.label}
              </MenuItem>
            ))}
          </Select>
          {errors.organisationCategory && (
            <FormHelperText>{errors.organisationCategory}</FormHelperText>
          )}
        </FormControl>
      </Box>

      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.agreeTerms}
              onChange={(e) => onChange("agreeTerms", e.target.checked)}
              sx={{ color: "#8D6E63", "&.Mui-checked": { color: "#6D4C41" } }}
            />
          }
          label={
            <Typography
              variant="body2"
              sx={{ color: "#5D4037", fontSize: "0.82rem" }}
            >
              I agree to the partnership terms and Muzukuru&apos;s privacy
              policy. I understand that Muzukuru will contact me to activate my
              partnership account.
            </Typography>
          }
        />
        {errors.agreeTerms && (
          <FormHelperText error sx={{ ml: 4 }}>
            {errors.agreeTerms}
          </FormHelperText>
        )}
      </Box>

      <Box sx={{ textAlign: "right" }}>
        <Button
          variant="contained"
          size="large"
          onClick={onNext}
          sx={{
            backgroundColor: "#724E41",
            px: 5,
            py: 1.2,
            borderRadius: "24px",
            "&:hover": { backgroundColor: "#4E342E" },
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}
